import { Link } from 'react-router-dom';
import { getPublished } from '@/data/loader';
import { useI18n } from '@/hooks/useI18n';
import { useTranslation } from '@/hooks/useTranslation';
import { EntityCard } from '@/components/EntityCard';
import { PraiasSEO } from '@/components/SEO';
import { Container } from '@/components/Container';
import { OptimizedImage } from '@/components/OptimizedImage';
import { RichText } from '@/components/RichText';
import { AuthorBox } from '@/components/AuthorBox';
import { defaultAuthor } from '@/data/author';

const PROFILES = ['families', 'surf', 'snorkel', 'solitude'] as const;
const PROFILE_ICONS: Record<string, string> = {
  families: '👨‍👩‍👧',
  surf: '🏄',
  snorkel: '🤿',
  solitude: '🌿',
};
const FAQ_COUNT = 8;
const TIPS_COUNT = 4;

export function PraiasPage() {
  const { lang } = useI18n();
  const { t } = useTranslation();
  const praias = getPublished('praia', lang);

  return (
    <>
      <PraiasSEO praias={praias} />
      <div>
        {/* Hero */}
        <div className="relative mb-10">
          <div className="relative h-48 sm:h-56">
            <OptimizedImage
              src="/images/hero-bg.jpg"
              alt="Praias da Ilha do Mel"
              className="w-full h-full object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-sand-100/95 via-sand-100/40 to-transparent" />
          </div>
          <div className="absolute bottom-0 left-0 right-0 pb-6">
            <Container>
              <h1 className="font-display text-4xl sm:text-5xl text-warm-dark">
                {t('page.praias.h1')}
              </h1>
            </Container>
          </div>
        </div>

        <Container className="pb-20">
          {/* Intro (com link para a home via RichText) */}
          <div className="max-w-3xl mb-16 font-body text-warm-gray leading-relaxed [&_p]:mb-4 [&_a]:text-teal-deep [&_a]:underline">
            <RichText text={t('page.praias.intro')} />
          </div>

          {/* Tabela Comparativa */}
          <section className="mb-16" id="comparativo">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2">
              {t('page.praias.compare.title')}
            </h2>
            <p className="font-body text-warm-gray mb-6">{t('page.praias.compare.subtitle')}</p>
            <div className="overflow-x-auto rounded-xl border border-sand-200 shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-teal-deep text-white">
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">
                      {t('page.praias.compare.colName')}
                    </th>
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">
                      {t('page.praias.compare.colMar')}
                    </th>
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">
                      {t('page.praias.compare.colDifficulty')}
                    </th>
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">
                      {t('page.praias.compare.colIdeal')}
                    </th>
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">
                      {t('page.praias.compare.colAccess')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-sand-100">
                  {praias.map((praia: any, i: number) => {
                    const tags = [
                      praia.kidsFriendly && 'Famílias',
                      praia.surfFriendly && 'Surf',
                      praia.snorkelFriendly && 'Snorkel',
                      !praia.kidsFriendly && !praia.surfFriendly && !praia.snorkelFriendly && 'Natureza',
                    ].filter(Boolean).join(', ');
                    return (
                      <tr key={praia.handle} className={i % 2 === 0 ? 'bg-white' : 'bg-sand-50'}>
                        <td className="px-4 py-3 font-body font-medium text-teal-deep whitespace-nowrap">
                          <Link to={`/praias/${praia.handle}`} className="hover:underline">
                            {praia.title}
                          </Link>
                        </td>
                        <td className="px-4 py-3 font-body text-warm-gray">{praia.waveHeight}</td>
                        <td className="px-4 py-3 font-body text-warm-gray">{praia.difficulty}</td>
                        <td className="px-4 py-3 font-body text-warm-gray">{tags}</td>
                        <td className="px-4 py-3 font-body text-warm-gray text-xs">{praia.access}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Cards de praias */}
          <section className="mb-16" id="praias">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2">
              {t('page.praias.allBeaches.title')}
            </h2>
            <p className="font-body text-warm-gray mb-8">{t('page.praias.allBeaches.subtitle')}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {praias.map((praia: any) => (
                <EntityCard key={praia.handle} entity={praia} category="praias" />
              ))}
            </div>
          </section>

          {/* Qual praia para você */}
          <section className="mb-16 bg-sand-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-12 rounded-2xl" id="perfis">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2 text-center">
              {t('page.praias.profiles.title')}
            </h2>
            <p className="font-body text-warm-gray mb-10 text-center">
              Escolha pela experiência que você busca
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {PROFILES.map(key => (
                <div key={key} className="bg-white rounded-xl p-6 shadow-sm border border-sand-200">
                  <div className="text-3xl mb-3">{PROFILE_ICONS[key]}</div>
                  <h3 className="font-display text-xl text-warm-dark mb-2">
                    {t(`page.praias.profiles.${key}.title`)}
                  </h3>
                  <p className="font-body text-warm-gray text-sm leading-relaxed mb-4">
                    {t(`page.praias.profiles.${key}.desc`)}
                  </p>
                  <span className="font-body text-xs font-semibold text-teal-deep bg-teal-light/10 px-3 py-1 rounded-full">
                    {t(`page.praias.profiles.${key}.beaches`)}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Dicas práticas */}
          <section className="mb-16" id="dicas">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-8">
              {t('page.praias.tips.title')}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {Array.from({ length: TIPS_COUNT }, (_, i) => (
                <div
                  key={i}
                  className="flex gap-4 p-5 bg-teal-light/5 rounded-xl border border-teal-light/20"
                >
                  <span className="text-teal-deep font-bold flex-shrink-0 mt-0.5">✓</span>
                  <p className="font-body text-warm-gray leading-relaxed">{t(`page.praias.tips.${i}`)}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="max-w-3xl" id="faq">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-8">
              {t('page.praias.faq.title')}
            </h2>
            <div className="space-y-3">
              {Array.from({ length: FAQ_COUNT }, (_, i) => (
                <details
                  key={i}
                  className="group bg-white border border-sand-200 rounded-lg overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-sand-50 list-none">
                    <span className="font-display text-base text-warm-dark pr-4">
                      {t(`page.praias.faq.items.${i}.q`)}
                    </span>
                    <span className="text-teal-deep flex-shrink-0 transition-transform duration-200 group-open:rotate-180">
                      ▼
                    </span>
                  </summary>
                  <div className="px-4 pb-4 font-body text-warm-gray text-sm leading-relaxed">
                    {t(`page.praias.faq.items.${i}.a`)}
                  </div>
                </details>
              ))}
            </div>
          </section>

          <AuthorBox author={defaultAuthor} />
        </Container>
      </div>
    </>
  );
}
